import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import ClinicalHtmlEditor from '../clinic/ClinicalHtmlEditor';
import { sanitizeClinicalHtml } from '../../utils/clinicalHtml';
import {
  canAddEvolutionAddition,
  canEditEvolutionAddition,
  formatEvolutionAdditionDate,
  type EvolutionAddition,
} from '../../utils/evolutionAdditionPolicy';
import type { EvolutionRecord, EvolutionViewer } from '../../utils/evolutionRecordPolicy';
import {
  evolutionCaptionMutedSx,
  evolutionSectionHeadingSx,
  evolutionSectionPaperSx,
} from './evolutionTheme';
import { evolutionAdditionItemSx, evolutionAdditionSurfaceBg } from './evolutionAdditionsStyles';
import EvolutionAdditionFileAttachments, {
  EvolutionAdditionAttachmentsReadonly,
} from './EvolutionAdditionFileAttachments';

type Props = {
  evolutionId: string | null;
  evolutionSnapshot: EvolutionRecord | null;
  patientId?: string | null;
  viewer: EvolutionViewer;
  professionalId: string | null;
  onError?: (message: string) => void;
  /** `flat` dentro de dialog; `paper` na página de evolução. */
  variant?: 'paper' | 'flat';
  compact?: boolean;
};

export default function EvolutionAdditionsSection({
  evolutionId,
  evolutionSnapshot,
  patientId: patientIdProp,
  viewer,
  professionalId,
  onError,
  variant = 'paper',
  compact = false,
}: Props) {
  const patientId =
    String(patientIdProp || '').trim()
    || String((evolutionSnapshot as { patient_id?: string } | null)?.patient_id || '').trim()
    || String((evolutionSnapshot as { patient?: { id?: string } } | null)?.patient?.id || '').trim()
    || '';
  const [items, setItems] = useState<EvolutionAddition[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EvolutionAddition | null>(null);
  const [bodyHtml, setBodyHtml] = useState('');
  const [addedAtLocal, setAddedAtLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingUploadAdditionId, setPendingUploadAdditionId] = useState<string | null>(null);
  const [attachmentSession, setAttachmentSession] = useState(0);

  const canAdd = Boolean(evolutionId && canAddEvolutionAddition(viewer, evolutionSnapshot));

  const unwrapAdditionRow = (res: { data?: unknown }) => {
    const body = res?.data as { data?: EvolutionAddition; success?: boolean } | EvolutionAddition | undefined;
    if (body && typeof body === 'object' && 'data' in body && body.data) return body.data;
    return body as EvolutionAddition;
  };

  const load = useCallback(async () => {
    if (!evolutionId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/evolutions/${evolutionId}/additions`));
      setItems(data?.data || []);
    } catch (e: any) {
      onError?.(e?.response?.data?.error || 'Não foi possível carregar informações adicionadas.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [evolutionId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setPendingUploadAdditionId(null);
    setAttachmentSession((n) => n + 1);
    setBodyHtml('');
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setAddedAtLocal(
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`,
    );
    setDialogOpen(true);
  };

  const openEdit = (row: EvolutionAddition) => {
    setEditing(row);
    setPendingUploadAdditionId(null);
    setAttachmentSession((n) => n + 1);
    setBodyHtml(row.body_html || '');
    const d = row.added_at ? new Date(row.added_at) : new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setAddedAtLocal(
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
    );
    setDialogOpen(true);
  };

  const save = async () => {
    if (!evolutionId || !professionalId) return;
    const html = sanitizeClinicalHtml(bodyHtml);
    if (!html.replace(/<[^>]+>/g, '').trim()) {
      onError?.('Informe o texto da informação adicionada.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        body_html: html,
        professional_id: professionalId,
        added_at: addedAtLocal ? new Date(addedAtLocal).toISOString() : undefined,
      };
      let savedRow: EvolutionAddition | null = editing;
      if (editing?.id) {
        const { data } = await axios.put(
          getApiUrl(`/clinic/evolutions/${evolutionId}/additions/${editing.id}`),
          payload,
        );
        savedRow = unwrapAdditionRow({ data }) || editing;
      } else {
        const { data } = await axios.post(getApiUrl(`/clinic/evolutions/${evolutionId}/additions`), payload);
        savedRow = unwrapAdditionRow({ data }) || null;
      }
      if (savedRow?.id) {
        setEditing(savedRow);
        setPendingUploadAdditionId(String(savedRow.id));
      }
      await load();
    } catch (e: any) {
      onError?.(e?.response?.data?.error || 'Não foi possível salvar a informação adicionada.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: EvolutionAddition) => {
    if (!evolutionId || !row.id) return;
    if (!window.confirm('Excluir esta informação adicionada?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/evolutions/${evolutionId}/additions/${row.id}`));
      await load();
    } catch (e: any) {
      onError?.(e?.response?.data?.error || 'Não foi possível excluir.');
    }
  };

  if (!evolutionId) return null;

  const shellSx =
    variant === 'flat'
      ? { bgcolor: 'transparent', boxShadow: 'none', border: 'none', mb: 0 }
      : { ...evolutionSectionPaperSx, bgcolor: evolutionAdditionSurfaceBg };

  const content = (
    <>
      <Box sx={{ px: variant === 'flat' ? 0 : 2, pt: variant === 'flat' ? 0 : 2, pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ ...evolutionSectionHeadingSx, fontSize: compact ? '0.7rem' : undefined }}>
            INFORMAÇÕES ADICIONADAS
          </Typography>
          <Typography variant="caption" sx={evolutionCaptionMutedSx}>
            Complementos datados após a conclusão da evolução, sem alterar o texto original do registro.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!canAdd}
          onClick={openNew}
        >
          Adicionar
        </Button>
      </Box>
      <Box sx={{ px: variant === 'flat' ? 0 : 2, pb: variant === 'flat' ? 0 : 2 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Carregando…</Typography>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma informação adicionada nesta evolução.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((row) => {
              const editable = canEditEvolutionAddition(viewer, row);
              return (
                <Box key={row.id} sx={evolutionAdditionItemSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      {formatEvolutionAdditionDate(row.added_at)}
                      {row.professional?.full_name ? ` · ${row.professional.full_name}` : ''}
                    </Typography>
                    <Box>
                      {editable && (
                        <>
                          <IconButton size="small" aria-label="Editar" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" aria-label="Excluir" onClick={() => void remove(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box
                    className="clinical-html"
                    sx={{
                      fontSize: compact ? '0.75rem' : 14,
                      lineHeight: 1.55,
                      '& p': { margin: '0 0 0.5rem' },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(row.body_html || '') }}
                  />
                  <EvolutionAdditionAttachmentsReadonly
                    documents={row.documents}
                    images={row.images}
                    compact={compact}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{editing?.id ? 'Editar informação adicionada' : 'Nova informação adicionada'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Data e hora do acréscimo"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={addedAtLocal}
            onChange={(e) => setAddedAtLocal(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <ClinicalHtmlEditor
            label="Conteúdo"
            minRows={8}
            value={bodyHtml}
            onChange={setBodyHtml}
            placeholder="Descreva a informação complementar…"
          />
          {evolutionId ? (
            <EvolutionAdditionFileAttachments
              key={attachmentSession}
              patientId={patientId}
              evolutionId={evolutionId}
              additionId={editing?.id || null}
              professionalId={professionalId}
              pendingUploadAdditionId={pendingUploadAdditionId}
              onPendingUploadComplete={() => setPendingUploadAdditionId(null)}
              disabled={Boolean(editing?.id) && !canEditEvolutionAddition(viewer, editing)}
              onError={onError}
              onChanged={() => void load()}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Fechar</Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving}>
            {editing?.id ? 'Salvar alterações' : 'Salvar e anexar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (variant === 'flat') {
    return <Box sx={shellSx}>{content}</Box>;
  }
  return (
    <Paper elevation={0} sx={shellSx}>
      {content}
    </Paper>
  );
}
